import type { Vec2 } from '../utils/vec2.js';

import type { PathGrid } from './Grid.js';
import { cellCenter, isBlocked, worldToCell } from './Grid.js';

// 8-way neighbor deltas. Diagonals must not corner-cut through walls — checked below.
const NEIGHBORS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

const SQRT2 = Math.SQRT2;

// Octile distance — admissible/consistent for 8-way movement; never overestimates.
function octile(ax: number, ay: number, bx: number, by: number): number {
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  return Math.max(dx, dy) + (SQRT2 - 1) * Math.min(dx, dy);
}

// Returns waypoints in world coordinates (cell centers along the path, with the actual
// goal pixel as the final waypoint). Empty array means no path; the caller should clear
// any active order rather than spinning.
export function aStar(grid: PathGrid, startWorld: Vec2, goalWorld: Vec2): Vec2[] {
  const startCell = worldToCell(grid, startWorld.x, startWorld.y);
  const goalCell = worldToCell(grid, goalWorld.x, goalWorld.y);
  if (isBlocked(grid, startCell.cx, startCell.cy)) return [];
  if (isBlocked(grid, goalCell.cx, goalCell.cy)) return [];

  const total = grid.cellsX * grid.cellsY;
  const gScore = new Float64Array(total);
  gScore.fill(Number.POSITIVE_INFINITY);
  const cameFrom = new Int32Array(total);
  cameFrom.fill(-1);
  const closed = new Uint8Array(total);

  const startIdx = startCell.cy * grid.cellsX + startCell.cx;
  const goalIdx = goalCell.cy * grid.cellsX + goalCell.cx;
  gScore[startIdx] = 0;

  const heap = new BinaryHeap();
  heap.push(startIdx, octile(startCell.cx, startCell.cy, goalCell.cx, goalCell.cy));

  while (heap.size > 0) {
    const curIdx = heap.popMinIndex();
    if (curIdx === goalIdx) {
      return reconstruct(grid, cameFrom, goalIdx, goalWorld);
    }
    if (closed[curIdx] === 1) continue;
    closed[curIdx] = 1;
    const cy = Math.floor(curIdx / grid.cellsX);
    const cx = curIdx - cy * grid.cellsX;

    for (const n of NEIGHBORS) {
      const dx = n[0];
      const dy = n[1];
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= grid.cellsX || ny >= grid.cellsY) continue;
      const nIdx = ny * grid.cellsX + nx;
      if (closed[nIdx] === 1) continue;
      if (grid.blocked[nIdx] === 1) continue;
      // No corner cutting: a diagonal step is forbidden when either of the cardinal
      // neighbors it shaves past is a wall.
      if (dx !== 0 && dy !== 0) {
        if (grid.blocked[cy * grid.cellsX + (cx + dx)] === 1) continue;
        if (grid.blocked[(cy + dy) * grid.cellsX + cx] === 1) continue;
      }
      const stepCost = dx !== 0 && dy !== 0 ? SQRT2 : 1;
      const curG = gScore[curIdx] ?? Number.POSITIVE_INFINITY;
      const tentativeG = curG + stepCost;
      const neighborG = gScore[nIdx] ?? Number.POSITIVE_INFINITY;
      if (tentativeG < neighborG) {
        cameFrom[nIdx] = curIdx;
        gScore[nIdx] = tentativeG;
        const f = tentativeG + octile(nx, ny, goalCell.cx, goalCell.cy);
        heap.push(nIdx, f);
      }
    }
  }
  return [];
}

function reconstruct(
  grid: PathGrid,
  cameFrom: Int32Array,
  goalIdx: number,
  goalWorld: Vec2,
): Vec2[] {
  const idxs: number[] = [];
  let i = goalIdx;
  while (i !== -1) {
    idxs.push(i);
    i = cameFrom[i] ?? -1;
  }
  idxs.reverse();
  const waypoints: Vec2[] = [];
  // Drop the start cell — squad is already there. The final waypoint uses the actual
  // pixel goal (not its cell center) so the squad lands where the user clicked.
  for (let k = 1; k < idxs.length - 1; k++) {
    const idx = idxs[k]!;
    const cy = Math.floor(idx / grid.cellsX);
    const cx = idx - cy * grid.cellsX;
    waypoints.push(cellCenter(grid, cx, cy));
  }
  waypoints.push({ x: goalWorld.x, y: goalWorld.y });
  return waypoints;
}

// Min-heap keyed by f. Stable enough for V0.2 — ties resolve by insertion order, which
// keeps the algorithm deterministic from a given seed even with no RNG involvement.
class BinaryHeap {
  private idxs: number[] = [];
  private fs: number[] = [];

  get size(): number {
    return this.idxs.length;
  }

  push(idx: number, f: number): void {
    this.idxs.push(idx);
    this.fs.push(f);
    this.bubbleUp(this.idxs.length - 1);
  }

  popMinIndex(): number {
    const idx = this.idxs[0]!;
    const lastIdx = this.idxs.pop()!;
    const lastF = this.fs.pop()!;
    if (this.idxs.length > 0) {
      this.idxs[0] = lastIdx;
      this.fs[0] = lastF;
      this.sinkDown(0);
    }
    return idx;
  }

  private bubbleUp(i: number): void {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.fs[parent]! <= this.fs[i]!) break;
      this.swap(parent, i);
      i = parent;
    }
  }

  private sinkDown(i: number): void {
    const n = this.idxs.length;
    for (;;) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let best = i;
      if (l < n && this.fs[l]! < this.fs[best]!) best = l;
      if (r < n && this.fs[r]! < this.fs[best]!) best = r;
      if (best === i) return;
      this.swap(best, i);
      i = best;
    }
  }

  private swap(a: number, b: number): void {
    const ti = this.idxs[a]!;
    const tf = this.fs[a]!;
    this.idxs[a] = this.idxs[b]!;
    this.fs[a] = this.fs[b]!;
    this.idxs[b] = ti;
    this.fs[b] = tf;
  }
}
